import React from 'react';

function formatCurrency(value) {
  if (!value) return '—';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function formatNumber(value) {
  return value?.toLocaleString() || '—';
}

function RoofDetailsDialog({ lead, onClose, onRelookup }) {
  if (!lead) return null;

  const roofSqft = lead.roofSqft || lead.estRoofSqft;
  const isMeasured = lead.roofSqftMeasured;

  // Calculate roofing squares (1 square = 100 sqft)
  const roofSquares = roofSqft ? Math.ceil(roofSqft / 100) : null;

  // Get all cost estimates
  const costs = {
    asphalt: lead.roofCostAsphalt || lead.estRoofCostLow,
    metal: lead.roofCostMetal || lead.estRoofCostHigh,
    tile: lead.roofCostTile
  };

  return (
    <div className="roof-dialog-overlay" onClick={onClose}>
      <div className="roof-dialog" onClick={e => e.stopPropagation()}>
        <button className="roof-dialog-close" onClick={onClose}>&times;</button>

        <div className="roof-dialog-header">
          <h2>Roof Estimate</h2>
          <p className="roof-dialog-address">{lead.streetAddress}</p>
          <p className="roof-dialog-city">{lead.city}, {lead.state} {lead.zip}</p>
        </div>

        {/* Owner Info */}
        <div className="roof-section">
          <h3>Property Owner</h3>
          <div className="roof-owner">
            <span className="owner-name">{lead.ownerName || 'Unknown'}</span>
            {lead.ownerOccupied && <span className="owner-badge">Owner Occupied</span>}
          </div>
        </div>

        {/* Roof Measurements */}
        <div className="roof-section roof-measurements">
          <h3>
            Roof Measurements
            {isMeasured && <span className="satellite-badge">📡 Satellite</span>}
          </h3>

          <div className="roof-stats-grid">
            <div className="roof-stat">
              <span className="stat-value">{formatNumber(roofSqft)}</span>
              <span className="stat-label">Roof Sqft</span>
            </div>
            <div className="roof-stat">
              <span className="stat-value">{roofSquares || '—'}</span>
              <span className="stat-label">Squares</span>
            </div>
            {lead.roofPitch && (
              <div className="roof-stat">
                <span className="stat-value">{lead.roofPitch.toFixed(1)}°</span>
                <span className="stat-label">Pitch</span>
              </div>
            )}
            {lead.roofSegments && (
              <div className="roof-stat">
                <span className="stat-value">{lead.roofSegments}</span>
                <span className="stat-label">Segments</span>
              </div>
            )}
          </div>

          <div className="measurement-note">
            {isMeasured ? (
              <span className="measured">✓ Measured from satellite imagery</span>
            ) : (
              <span className="estimated">Estimated from building sqft ({lead.sqft} sqft ÷ {lead.floorCount || 1} floors)</span>
            )}
          </div>
        </div>

        {/* Roof Details */}
        <div className="roof-section">
          <h3>Roof Details</h3>
          <div className="roof-details-grid">
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span className="detail-value">{lead.roofType || 'Unknown'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Age</span>
              <span className="detail-value">
                {lead.roofAge ? `${lead.roofAge} years` : 'Unknown'}
                {lead.needsRoofReplacement && <span className="warning-badge">⚠️ Needs Replacement</span>}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Year Built</span>
              <span className="detail-value">{lead.yearBuilt || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Cost Estimates */}
        <div className="roof-section roof-costs">
          <h3>Roof Replacement Estimates</h3>

          <div className="cost-cards">
            <div className="cost-card asphalt">
              <div className="cost-type">Asphalt Shingles</div>
              <div className="cost-value">{formatCurrency(costs.asphalt)}</div>
              <div className="cost-per-sqft">
                {roofSqft && costs.asphalt ? `$${(costs.asphalt / roofSqft).toFixed(2)}/sqft` : ''}
              </div>
            </div>

            <div className="cost-card metal">
              <div className="cost-type">Standing Seam Metal</div>
              <div className="cost-value">{formatCurrency(costs.metal)}</div>
              <div className="cost-per-sqft">
                {roofSqft && costs.metal ? `$${(costs.metal / roofSqft).toFixed(2)}/sqft` : ''}
              </div>
            </div>

            {costs.tile && (
              <div className="cost-card tile">
                <div className="cost-type">Clay/Concrete Tile</div>
                <div className="cost-value">{formatCurrency(costs.tile)}</div>
                <div className="cost-per-sqft">
                  {roofSqft && costs.tile ? `$${(costs.tile / roofSqft).toFixed(2)}/sqft` : ''}
                </div>
              </div>
            )}
          </div>

          <div className="cost-breakdown">
            <details>
              <summary>Cost Breakdown (per square)</summary>
              <div className="breakdown-table">
                <div className="breakdown-row header">
                  <span>Material</span>
                  <span>$/Square</span>
                  <span>Total ({roofSquares} sq)</span>
                </div>
                <div className="breakdown-row">
                  <span>3-Tab Shingles</span>
                  <span>$350-450</span>
                  <span>{roofSquares ? `$${(roofSquares * 400).toLocaleString()}` : '—'}</span>
                </div>
                <div className="breakdown-row">
                  <span>Architectural</span>
                  <span>$450-600</span>
                  <span>{roofSquares ? `$${(roofSquares * 525).toLocaleString()}` : '—'}</span>
                </div>
                <div className="breakdown-row">
                  <span>Premium Shingles</span>
                  <span>$600-800</span>
                  <span>{roofSquares ? `$${(roofSquares * 700).toLocaleString()}` : '—'}</span>
                </div>
                <div className="breakdown-row">
                  <span>Metal (Standing)</span>
                  <span>$900-1400</span>
                  <span>{roofSquares ? `$${(roofSquares * 1150).toLocaleString()}` : '—'}</span>
                </div>
              </div>
            </details>
          </div>
        </div>

        {/* Property Info */}
        <div className="roof-section">
          <h3>Property Details</h3>
          <div className="property-grid">
            <div className="prop-item">
              <span className="prop-icon">🏠</span>
              <span className="prop-value">{formatNumber(lead.sqft)} sqft</span>
              <span className="prop-label">Living Area</span>
            </div>
            <div className="prop-item">
              <span className="prop-icon">💰</span>
              <span className="prop-value">{formatCurrency(lead.assessedValue)}</span>
              <span className="prop-label">Assessed Value</span>
            </div>
            {lead.bedrooms && (
              <div className="prop-item">
                <span className="prop-icon">🛏️</span>
                <span className="prop-value">{lead.bedrooms}bd / {lead.bathrooms}ba</span>
                <span className="prop-label">Bed/Bath</span>
              </div>
            )}
            {lead.lotSize && (
              <div className="prop-item">
                <span className="prop-icon">📐</span>
                <span className="prop-value">{formatNumber(lead.lotSize)} sqft</span>
                <span className="prop-label">Lot Size</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="roof-dialog-actions">
          {onRelookup && (
            <button className="relookup-action" onClick={() => { onRelookup(); onClose(); }}>
              🔄 Refresh Data
            </button>
          )}
          <button className="close-action" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default RoofDetailsDialog;
